import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Row } from 'write-excel-file';

import type { CodingResult } from 'src/types/dashboard';
import { groupCalculatedCodingResults } from 'src/components/dashboard/DashboardContext/CodingResultsContext/groupCalculatedCodingResult';
import type { WidgetExportOptions } from 'src/components/dashboard/DashboardPage/components/WidgetFrame/useWidgetExport';

type UseExportOptionsProps = {
  data?: CodingResult[];
  comparisonData?: CodingResult[];
} & Pick<WidgetExportOptions, 'widgetName' | 'queryFilters'>;

const useMapRows = () => {
  const { t } = useTranslation('dashboard');

  return useCallback(
    (data: CodingResult[]) => [
      [
        { value: t('OpenComments.topics'), fontWeight: 'bold' },
        { value: t('OpenComments.subTopics'), fontWeight: 'bold' },
        { value: t('OpenComments.negativeTopics'), fontWeight: 'bold' },
        { value: t('OpenComments.neutralTopics'), fontWeight: 'bold' },
        { value: t('OpenComments.positiveTopics'), fontWeight: 'bold' },
      ] as Row,
      ...groupCalculatedCodingResults(data).flatMap<Row>(
        ({ subTopics, label }) =>
          subTopics
            ? subTopics.map<Row>((subTopic, index) => [
                { value: index === 0 ? label : '' },
                { value: subTopic.label },
                { value: subTopic.negativeCount },
                { value: subTopic.neutralCount },
                { value: subTopic.positiveCount },
              ])
            : [],
      ),
    ],
    [t],
  );
};

export const useExportOptions = ({
  comparisonData,
  data,
  widgetName,
  queryFilters,
}: UseExportOptionsProps): WidgetExportOptions => {
  const mapRows = useMapRows();

  return useMemo(() => {
    const rows = mapRows(data || []);
    const comparisonRows = comparisonData ? mapRows(comparisonData) : undefined;

    return {
      widgetName,
      rows,
      comparisonRows,
      queryFilters,
    };
  }, [data, widgetName, queryFilters, mapRows]);
};
